import React from 'react'
import { IconButton, Popover, Box, Typography, Tooltip } from '@mui/material'
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined'
import CheckIcon from '@mui/icons-material/Check'
import {
  DEFAULT_THEME,
  THEME_ORDER,
  themeLabels,
  themePreviewHex,
  type Theme,
} from '@theme/theme'
import useThemeSwitcher from '@theme/useThemeSwitcher'

const SWATCH_BOX = {
  width: 44,
  height: 44,
  borderRadius: '10px',
  cursor: 'pointer',
  border: '2px solid',
  transition: 'transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 6px 16px rgba(15, 23, 42, 0.12)',
  },
} as const

type ThemePickerPopoverProps = {
  /** `header`: nút trắng trên navbar tối. `surface`: nút trên nền sáng (login…). */
  variant?: 'header' | 'surface'
  /** Controlled theme — dùng khi parent cần re-render theo màu (login stage). */
  currentTheme?: Theme
  setTheme?: (id: Theme) => void
  /** Màu accent cho nút surface (primary hiện tại). */
  accentColor?: string
  surfaceBorder?: string
  surfaceBg?: string
}

/** Theme picker — port từ ytl-logistics-frontend ThemePickerPopover. */
const ThemePickerPopover: React.FC<ThemePickerPopoverProps> = ({
  variant = 'header',
  currentTheme: controlledTheme,
  setTheme: controlledSetTheme,
  accentColor,
  surfaceBorder,
  surfaceBg,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null)
  const internal = useThemeSwitcher()
  const currentTheme = controlledTheme ?? internal.currentTheme
  const setTheme = controlledSetTheme ?? internal.setTheme
  const open = Boolean(anchorEl)
  const activeId = currentTheme in themePreviewHex ? currentTheme : DEFAULT_THEME
  const isSurface = variant === 'surface'
  const primary = accentColor ?? themePreviewHex[activeId]
  const border = surfaceBorder ?? 'rgba(15, 23, 42, 0.12)'
  const hoverBg = surfaceBg ?? '#f8fafc'

  return (
    <>
      <Tooltip title="Giao diện">
        <IconButton
          type="button"
          size="medium"
          color="inherit"
          aria-label="Giao diện"
          aria-haspopup="true"
          aria-expanded={open}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={
            isSurface
              ? {
                  color: primary,
                  border: `1px solid ${border}`,
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                  boxShadow: '0 2px 8px rgba(15, 23, 42, 0.08)',
                  '&:hover': {
                    backgroundColor: hoverBg,
                  },
                }
              : {
                  border: '1px solid rgba(255, 255, 255, 0.22)',
                  borderRadius: '6px',
                  backgroundColor: 'rgba(255, 255, 255, 0.06)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.14)',
                  },
                }
          }
        >
          <PaletteOutlinedIcon />
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 1,
            p: 1.75,
            borderRadius: 2,
            minWidth: 200,
            boxShadow: '0 12px 40px rgba(15, 23, 42, 0.12)',
          },
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.25, color: 'text.primary' }}>
          Giao diện
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 44px)',
            gap: '10px',
            justifyContent: 'center',
          }}
          role="listbox"
          aria-label="Chọn chủ đề màu"
        >
          {THEME_ORDER.map((id) => {
            const selected = id === activeId
            return (
              <Box
                key={id}
                component="button"
                type="button"
                role="option"
                aria-selected={selected}
                title={themeLabels[id]}
                onClick={() => {
                  setTheme(id as Theme)
                  setAnchorEl(null)
                }}
                sx={{
                  ...SWATCH_BOX,
                  bgcolor: themePreviewHex[id],
                  borderColor: selected ? '#ffffff' : 'rgba(15,23,42,0.12)',
                  boxShadow: selected
                    ? '0 0 0 2px rgba(255,255,255,0.95), 0 0 0 4px rgba(15,23,42,0.15)'
                    : 'none',
                }}
              >
                {selected ? <CheckIcon sx={{ color: '#fff', fontSize: 22 }} /> : null}
              </Box>
            )
          })}
        </Box>
      </Popover>
    </>
  )
}

export default ThemePickerPopover
